import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Header } from "../components/shared";
import { CheckoutFooter } from "../components/checkout";
import { useTranslation } from "../hooks/useTranslation";
import { useCookieConsent } from "../context/CookieConsentContext";
import { MEDIA_URL } from "../config/constants";
import { SEO } from "../seo";
import "../styles/surf/SurfPage.css";

// Surf landing page + Miguel mobile-surf-camp waitlist.
// SCAFFOLD: layout/sections are structural placeholders for Lea to design and for
// real photos/video + copy to be dropped in. Striped blocks = media placeholders.

// Real photos wired into the existing media slots; every slot now has one.
// Paths are index-matched to the locale arrays, which are the same length and
// order in every language file (en/pt/es/fr/it).

// Every image on this page is served from the media CDN
// (MEDIA_URL -> media.suncatchers.pt), like the rest of the site. The originals
// also live in public/assets/images/surf/ as the source of truth for re-uploads.
const cdn = (path) => `${MEDIA_URL}/images/${path}`;

// Used twice: the Ericeira spot card and the waitlist backdrop.
const ERICEIRA = cdn("surf/ericeira-surf.jpg");

const SURF_MEDIA = {
  hero: cdn("surf/algarve-beachs-surf.jpg"),
  teacher: cdn("surf/miguel-sruf-teacher.jpeg"),
  // Portrait (4059x6088) — rendered in a portrait slot, see .surf-media--portrait.
  vans: cdn("surf/surf_van_suncatchers.jpg"),
  // 2160x1440 (3:2) — matches the landscape split slot. .webp is 680K vs 2.7M
  // for the identical .jpg, and is the variant Contact.jsx already uses.
  trip: cdn("adventure-awaits-banner-friends-campervan.webp"),
  // Backdrop for the waitlist form, applied as a CSS custom property in JSX
  // because css-loader would try to resolve a url() in the stylesheet.
  waitlistBackdrop: ERICEIRA,
  // Portrait product shots — letterboxed, never cropped (see .surf-brand-media).
  brands: [
    cdn("surf/semente-suncatchers-surfboard-boardonly.webp"),
    cdn("surf/vissla-wetsuit.webp"),
    cdn("surf/Flowt-surf-Premium76_Frontviee.png"),
  ],
  // Landscape location shots — cropped to fill (object-fit: cover).
  spots: [
    ERICEIRA,
    cdn("surf/baleal-beach-peniche.jpg"),
    cdn("surf/algarve-beachs-surf.jpg"),
  ],
};

// The reel is ~70MB, so it lives on YouTube rather than in the repo.
// Put the video id here (the "dQw4w9WgXcQ" part of the watch URL) and both the
// reel section and the hero pick it up. While it is empty the reel section
// shows a waiting note and the hero shows its still photo.
const SURF_YOUTUBE_ID = "";

// A 16:9 YouTube embed, mounted only once marketing cookies are accepted.
const SurfVideo = ({ videoId, title, placeholder }) => {
  const { preferences } = useCookieConsent();

  if (!videoId) {
    return (
      <div className="surf-media surf-video-frame" role="img" aria-label={placeholder}>
        <span className="surf-media-label">{placeholder}</span>
      </div>
    );
  }

  if (!preferences?.marketing) {
    return (
      <div className="surf-video-frame surf-video-frame--consent">
        <p className="surf-muted">{placeholder}</p>
        <a
          className="surf-link"
          href={`https://www.youtube.com/watch?v=${videoId}`}
          target="_blank"
          rel="noopener noreferrer"
        >
          {title} ↗
        </a>
      </div>
    );
  }

  return (
    <div className="surf-video-frame">
      <iframe
        className="surf-video-embed"
        src={`https://www.youtube-nocookie.com/embed/${videoId}?rel=0&modestbranding=1&playsinline=1`}
        title={title}
        allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
        allowFullScreen
        frameBorder="0"
      />
    </div>
  );
};

// Hero media: the still photo, or the YouTube reel once there is an id AND the
// visitor has accepted marketing cookies. YouTube sets tracking cookies even on
// the -nocookie domain once playback starts, so the iframe is never mounted
// before consent — the still photo stands in, which is why it is kept.
const SurfHeroMedia = ({ poster, label, videoId }) => {
  const { preferences } = useCookieConsent();
  const embed = Boolean(videoId) && Boolean(preferences?.marketing);

  if (!embed) {
    return (
      <SurfMedia className="surf-hero-media" src={poster} label={label} alt={label} eager />
    );
  }

  const params = new URLSearchParams({
    autoplay: "1",
    mute: "1",
    loop: "1",
    playlist: videoId, // required for loop to work on a single video
    controls: "0",
    playsinline: "1",
    rel: "0",
    modestbranding: "1",
  });

  return (
    <div className="surf-media surf-hero-media" role="img" aria-label={label}>
      <iframe
        className="surf-hero-video"
        src={`https://www.youtube-nocookie.com/embed/${videoId}?${params}`}
        title={label}
        allow="autoplay; encrypted-media; picture-in-picture"
        referrerPolicy="strict-origin-when-cross-origin"
        frameBorder="0"
      />
    </div>
  );
};

// Renders a real photo when one exists, otherwise the original striped placeholder.
const SurfMedia = ({ className = "", src, label, alt, eager = false }) => (
  <div className={["surf-media", className].filter(Boolean).join(" ")} role="img" aria-label={alt || label}>
    {src ? (
      <img
        className="surf-media-img"
        src={encodeURI(src)}
        alt={alt || ""}
        loading={eager ? "eager" : "lazy"}
        decoding="async"
      />
    ) : (
      <span className="surf-media-label">{label}</span>
    )}
  </div>
);

const SurfPage = () => {
  const navigate = useNavigate();
  const { t, getLocalizedPath } = useTranslation("surf");

  const pillars = t("intro.pillars", { returnObjects: true }) || [];
  const teacherPoints = t("teacher.points", { returnObjects: true }) || [];
  const brands = t("material.brands", { returnObjects: true }) || [];
  const vanModels = t("vans.models", { returnObjects: true }) || [];
  const vanReasons = t("vans.reasons", { returnObjects: true }) || [];
  const spots = t("spots.items", { returnObjects: true }) || [];
  const highlights = t("trip.highlights", { returnObjects: true }) || [];
  const priceIncludes = t("pricing.includes", { returnObjects: true }) || [];

  // Waitlist form
  const [form, setForm] = useState({
    name: "",
    email: "",
    phone: "",
    dates: "",
    days: "",
    level: "",
    message: "",
  });
  const [status, setStatus] = useState("idle"); // idle | submitting | success | error
  const onChange = (e) => setForm({ ...form, [e.target.name]: e.target.value });

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name || !form.email) return;
    setStatus("submitting");

    // Sent through the existing contact endpoint, which emails the team. There
    // is no dedicated surf-waitlist endpoint yet; the signup details are put in
    // the message body so nothing is lost until one exists.
    const [firstName, ...rest] = form.name.trim().split(/\s+/);
    const message = [
      "SURF CAMP WAITLIST SIGNUP",
      "",
      `Preferred dates: ${form.dates || "-"}`,
      `Trip length: ${form.days || "-"}`,
      `Surf level: ${form.level || "-"}`,
      "",
      `Message: ${form.message || "-"}`,
    ].join("\n");

    try {
      const response = await fetch(`${process.env.REACT_APP_API_URL}/api/v1/contact`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          ...(process.env.REACT_APP_API_KEY && { "X-API-Key": process.env.REACT_APP_API_KEY }),
        },
        body: JSON.stringify({
          firstName,
          // The contact endpoint requires a last name; signups give one field.
          lastName: rest.join(" ") || "-",
          email: form.email,
          phone: form.phone,
          message,
        }),
      });

      const data = await response.json().catch(() => ({}));
      if (!response.ok) {
        throw new Error(data.error || "Failed to send signup");
      }
      setStatus("success");
    } catch (err) {
      // Never report success for a signup that was not delivered.
      setStatus("error");
    }
  };

  const scrollToWaitlist = () => {
    const el = document.getElementById("surf-waitlist");
    if (el) el.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="surf-page">
      <SEO title={t("seo.title")} description={t("seo.description")} />
      <Header />

      {/* HERO */}
      <section className="surf-hero">
        <SurfHeroMedia
          poster={SURF_MEDIA.hero}
          label={t("hero.videoLabel")}
          videoId={SURF_YOUTUBE_ID}
        />
        <div className="surf-hero-overlay">
          <div className="surf-eyebrow">{t("hero.eyebrow")}</div>
          <h1 className="surf-hero-title">{t("hero.title")}</h1>
          <p className="surf-hero-subtitle">{t("hero.subtitle")}</p>
          <div className="surf-hero-cta">
            <button className="surf-btn surf-btn--primary" onClick={() => navigate(getLocalizedPath("/catalog"))}>
              {t("hero.ctaPrimary")}
            </button>
            <button className="surf-btn surf-btn--ghost" onClick={scrollToWaitlist}>
              {t("hero.ctaSecondary")}
            </button>
          </div>
        </div>
      </section>

      {/* INTRO — private teacher / private van / cultural activities + all ages */}
      <section className="surf-section surf-intro">
        <div className="surf-intro-head">
          <div className="surf-eyebrow">{t("intro.eyebrow")}</div>
          <h2 className="surf-h2">{t("intro.title")}</h2>
          <p className="surf-lead">{t("intro.lead")}</p>
        </div>
        <div className="surf-pillar-grid">
          {pillars.map((p, i) => (
            <div className="surf-pillar-card" key={i}>
              <h3 className="surf-h3">{p.title}</h3>
              <p className="surf-muted">{p.text}</p>
            </div>
          ))}
        </div>
        <div className="surf-callout">
          <h3 className="surf-h3">{t("intro.allAgesTitle")}</h3>
          <p className="surf-muted">{t("intro.allAges")}</p>
        </div>
      </section>

      {/* THE TEACHER */}
      <section className="surf-section surf-split surf-teacher">
        <SurfMedia
          src={SURF_MEDIA.teacher}
          label={t("teacher.photoLabel")}
          alt={t("teacher.photoLabel")}
        />
        <div className="surf-section-body">
          <div className="surf-eyebrow">{t("teacher.eyebrow")}</div>
          <h2 className="surf-h2">{t("teacher.title")}</h2>
          <p className="surf-lead">{t("teacher.text")}</p>
          <h3 className="surf-h3">{t("teacher.pointsTitle")}</h3>
          <ul className="surf-check-list">
            {teacherPoints.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
        </div>
      </section>

      {/* MATERIAL — Semente / Vissla / Flot */}
      <section className="surf-section surf-material">
        <div className="surf-intro-head">
          <div className="surf-eyebrow">{t("material.eyebrow")}</div>
          <h2 className="surf-h2">{t("material.title")}</h2>
          <p className="surf-lead">{t("material.lead")}</p>
        </div>
        <div className="surf-brand-grid">
          {brands.map((b, i) => (
            <div className="surf-brand-card" key={i}>
              <SurfMedia
                className="surf-brand-media"
                src={SURF_MEDIA.brands[i]}
                label={`PHOTO — ${b.name}`}
                alt={b.name}
              />
              <h3 className="surf-h3">{b.name}</h3>
              <div className="surf-brand-role">{b.role}</div>
              <p className="surf-muted">{b.text}</p>

              {/* Each brand lists its own range. A brand with no items yet
                  simply renders the card without a list. */}
              {Array.isArray(b.items) && b.items.length > 0 && (
                <div className="surf-brand-items">
                  {b.itemsTitle && <h4 className="surf-brand-items-title">{b.itemsTitle}</h4>}
                  <ul className="surf-board-list">
                    {b.items.map((item, j) => (
                      <li key={j}>
                        {item.url ? (
                          <a href={item.url} target="_blank" rel="noopener noreferrer">
                            {item.label}
                          </a>
                        ) : (
                          item.label
                        )}
                      </li>
                    ))}
                  </ul>
                </div>
              )}

              {b.link && (
                <a className="surf-link" href={b.link} target="_blank" rel="noopener noreferrer">
                  {b.linkLabel || b.name} ↗
                </a>
              )}
            </div>
          ))}
        </div>
      </section>

      {/* THE VANS — why they suit surfers */}
      <section className="surf-section surf-split surf-vans">
        <div className="surf-section-body">
          <div className="surf-eyebrow">{t("vans.eyebrow")}</div>
          <h2 className="surf-h2">{t("vans.title")}</h2>
          <p className="surf-lead">{t("vans.lead")}</p>
          {vanModels.map((m, i) => (
            <div className="surf-van-model" key={i}>
              <h3 className="surf-h3">{m.name}</h3>
              <p className="surf-muted">{m.text}</p>
            </div>
          ))}
          <h3 className="surf-h3">{t("vans.reasonsTitle")}</h3>
          <ul className="surf-check-list">
            {vanReasons.map((r, i) => <li key={i}>{r}</li>)}
          </ul>
        </div>
        <SurfMedia
          className="surf-media--portrait"
          src={SURF_MEDIA.vans}
          label={t("vans.photoLabel")}
          alt={t("vans.photoLabel")}
        />
      </section>

      {/* SURF SPOTS */}
      <section className="surf-section surf-spots">
        <h2 className="surf-h2 surf-center">{t("spots.title")}</h2>
        <p className="surf-lead surf-center">{t("spots.text")}</p>
        <div className="surf-spot-grid">
          {spots.map((s, i) => (
            <div className="surf-spot-card" key={i}>
              <SurfMedia
                className="surf-spot-media"
                src={SURF_MEDIA.spots[i]}
                label={`PHOTO — ${s.name}`}
                alt={s.name}
              />
              <h3 className="surf-h3">{s.name}</h3>
              <p className="surf-muted">{s.text}</p>
            </div>
          ))}
        </div>
      </section>

      {/* THE REEL — YouTube hosted (the file is ~70MB) */}
      <section className="surf-section surf-video">
        <div className="surf-intro-head">
          <div className="surf-eyebrow">{t("video.eyebrow")}</div>
          <h2 className="surf-h2">{t("video.title")}</h2>
          <p className="surf-lead">{t("video.text")}</p>
        </div>
        <SurfVideo videoId={SURF_YOUTUBE_ID} title={t("video.title")} placeholder={t("video.placeholder")} />
      </section>

      {/* MIGUEL MOBILE SURF CAMP */}
      <section className="surf-section surf-split surf-trip">
        <div className="surf-section-body">
          <div className="surf-eyebrow">{t("trip.eyebrow")}</div>
          <h2 className="surf-h2">{t("trip.title")}</h2>
          <p className="surf-lead">{t("trip.text")}</p>
          <ul className="surf-check-list">
            {highlights.map((h, i) => <li key={i}>{h}</li>)}
          </ul>
        </div>
        <SurfMedia
          className="surf-trip-media"
          src={SURF_MEDIA.trip}
          label={t("trip.photoLabel")}
          alt={t("trip.photoLabel")}
        />
      </section>

      {/* PRICING */}
      <section className="surf-section surf-pricing">
        <div className="surf-price-card">
          <div className="surf-eyebrow">{t("pricing.eyebrow")}</div>
          <h2 className="surf-h2">{t("pricing.title")}</h2>
          <p className="surf-lead">{t("pricing.lead")}</p>
          <div className="surf-price-figure">
            <span className="surf-price-from">{t("pricing.fromLabel")}</span>
            <span className="surf-price-amount">{t("pricing.amount")}</span>
            <span className="surf-price-unit">{t("pricing.unit")}</span>
          </div>
          <h3 className="surf-h3">{t("pricing.includesTitle")}</h3>
          <ul className="surf-check-list">
            {priceIncludes.map((p, i) => <li key={i}>{p}</li>)}
          </ul>
          <button className="surf-btn surf-btn--primary" onClick={scrollToWaitlist}>
            {t("hero.ctaSecondary")}
          </button>
        </div>
      </section>

      {/* WAITLIST */}
      <section
        className="surf-section surf-waitlist"
        id="surf-waitlist"
        style={{ "--surf-waitlist-image": `url("${encodeURI(SURF_MEDIA.waitlistBackdrop)}")` }}
      >
        <div className="surf-waitlist-card">
          <h2 className="surf-h2 surf-center">{t("waitlist.title")}</h2>
          <p className="surf-lead surf-center">{t("waitlist.subtitle")}</p>

          {status === "success" ? (
            <div className="surf-waitlist-success">{t("waitlist.success")}</div>
          ) : (
            <form className="surf-waitlist-form" onSubmit={handleSubmit}>
              <input className="surf-input" name="name" value={form.name} onChange={onChange} placeholder={t("waitlist.name")} required />
              <input className="surf-input" name="email" type="email" value={form.email} onChange={onChange} placeholder={t("waitlist.email")} required />
              <input className="surf-input" name="phone" value={form.phone} onChange={onChange} placeholder={t("waitlist.phone")} />
              <input className="surf-input" name="dates" value={form.dates} onChange={onChange} placeholder={t("waitlist.dates")} />
              <select className="surf-input" name="days" value={form.days} onChange={onChange}>
                <option value="">{t("waitlist.daysOptions.placeholder")}</option>
                <option value="weekend">{t("waitlist.daysOptions.weekend")}</option>
                <option value="five-days">{t("waitlist.daysOptions.fiveDays")}</option>
                <option value="one-week">{t("waitlist.daysOptions.oneWeek")}</option>
                <option value="two-weeks">{t("waitlist.daysOptions.twoWeeks")}</option>
                <option value="longer">{t("waitlist.daysOptions.longer")}</option>
              </select>
              <select className="surf-input" name="level" value={form.level} onChange={onChange}>
                <option value="">{t("waitlist.levels.placeholder")}</option>
                <option value="beginner">{t("waitlist.levels.beginner")}</option>
                <option value="intermediate">{t("waitlist.levels.intermediate")}</option>
                <option value="advanced">{t("waitlist.levels.advanced")}</option>
              </select>
              <textarea className="surf-input surf-textarea" name="message" value={form.message} onChange={onChange} placeholder={t("waitlist.message")} rows={3} />
              {status === "error" && <div className="surf-waitlist-error">{t("waitlist.error")}</div>}
              <button className="surf-btn surf-btn--primary surf-btn--full" type="submit" disabled={status === "submitting"}>
                {status === "submitting" ? t("waitlist.submitting") : t("waitlist.submit")}
              </button>
            </form>
          )}
        </div>
      </section>

      <CheckoutFooter />
    </div>
  );
};

export default SurfPage;
